import { createTheme, type Theme } from '@mui/material';
import { CONTENT_FONT_FAMILY_VAR } from './fonts';

/**
 * Help Center type: Readex Pro (the content-area font) in both languages, so
 * English and Arabic articles share one face and one weight scale.
 */
export const HELP_FONT_FAMILY_EN = CONTENT_FONT_FAMILY_VAR;

export const HELP_FONT_FAMILY_AR = CONTENT_FONT_FAMILY_VAR;

export const getHelpFontFamily = (language: string): string =>
  language.startsWith('ar') ? HELP_FONT_FAMILY_AR : HELP_FONT_FAMILY_EN;

/** Both languages use Readex Pro, so weights need no per-language adjustment. */
export const getHelpFontWeight = (
  _language: string,
  weight: 400 | 500 | 600
): 400 | 500 | 600 => weight;

export const HELP_KNOWLEDGE_CARD_TITLE_SX = {
  fontSize: '1rem',
  lineHeight: 1.45,
  letterSpacing: 0,
} as const;

export const HELP_KNOWLEDGE_CARD_TITLE_MIN_HEIGHT = '2.9rem';

export const HELP_KNOWLEDGE_CARD_LINK_SX = {
  fontSize: '0.8125rem',
  lineHeight: 1.45,
  letterSpacing: 0,
} as const;

export const HELP_BODY_SX = {
  fontSize: '0.9375rem',
  lineHeight: 1.75,
  letterSpacing: 0,
} as const;

/**
 * A start-adorned outlined input laid out with logical properties. The admin
 * has no RTL style plugin, so MUI's physical left padding / right margin would
 * put the search icon against the border in Arabic.
 */
export const HELP_SEARCH_FIELD_SX = {
  '& .MuiOutlinedInput-root.MuiInputBase-adornedStart': {
    paddingLeft: 0,
    paddingInlineStart: '12px',
  },
  '& .MuiOutlinedInput-root .MuiInputBase-inputAdornedStart': {
    paddingLeft: 0,
    paddingRight: 0,
    paddingInlineStart: 0,
    paddingInlineEnd: '14px',
  },
  '& .MuiInputAdornment-positionStart': {
    marginRight: 0,
    marginInlineEnd: '8px',
  },
} as const;

export const helpTextRenderingSx = {
  WebkitFontSmoothing: 'antialiased',
  MozOsxFontSmoothing: 'grayscale',
  textRendering: 'optimizeLegibility',
} as const;

export const getKnowledgeCardTitleSx = (language: string) => ({
  ...HELP_KNOWLEDGE_CARD_TITLE_SX,
  ...helpTextRenderingSx,
  fontFamily: getHelpFontFamily(language),
  fontWeight: getHelpFontWeight(language, 600),
  minHeight: HELP_KNOWLEDGE_CARD_TITLE_MIN_HEIGHT,
});

export const getKnowledgeCardLinkSx = (language: string) => ({
  ...HELP_KNOWLEDGE_CARD_LINK_SX,
  ...helpTextRenderingSx,
  fontFamily: getHelpFontFamily(language),
  fontWeight: getHelpFontWeight(language, 600),
});

const withHelpFontFamily = (parentTheme: Theme, language: string) => {
  const fontFamily = getHelpFontFamily(language);
  const { typography } = parentTheme;

  return {
    ...typography,
    fontFamily,
    h4: { ...typography.h4, fontFamily, letterSpacing: 0 },
    h6: { ...typography.h6, fontFamily, letterSpacing: 0 },
    subtitle1: {
      ...typography.subtitle1,
      fontFamily,
      fontSize: HELP_KNOWLEDGE_CARD_TITLE_SX.fontSize,
      lineHeight: HELP_KNOWLEDGE_CARD_TITLE_SX.lineHeight,
      letterSpacing: 0,
    },
    subtitle2: {
      ...typography.subtitle2,
      fontFamily,
      fontSize: HELP_KNOWLEDGE_CARD_LINK_SX.fontSize,
      lineHeight: HELP_KNOWLEDGE_CARD_LINK_SX.lineHeight,
      letterSpacing: 0,
    },
    body1: {
      ...typography.body1,
      fontFamily,
      fontSize: HELP_BODY_SX.fontSize,
      lineHeight: HELP_BODY_SX.lineHeight,
      letterSpacing: 0,
    },
    body2: { ...typography.body2, fontFamily, letterSpacing: 0 },
    caption: { ...typography.caption, fontFamily, letterSpacing: 0 },
    button: { ...typography.button, fontFamily, letterSpacing: 0 },
  };
};

export const createHelpTheme = (parentTheme: Theme, language: string) => {
  const direction = language.startsWith('ar') ? 'rtl' : 'ltr';
  const fontFamily = getHelpFontFamily(language);

  return createTheme(parentTheme, {
    direction,
    typography: withHelpFontFamily(parentTheme, language),
    components: {
      ...parentTheme.components,
      MuiCard: {
        styleOverrides: {
          root: {
            fontFamily,
            transform: 'none',
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            fontFamily,
            letterSpacing: 0,
            ...helpTextRenderingSx,
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            letterSpacing: 0,
            ...helpTextRenderingSx,
          },
        },
      },
    },
  });
};

export const helpTypographySx = (language: string) => {
  const fontFamily = getHelpFontFamily(language);

  return {
    fontFamily,
    ...helpTextRenderingSx,
    '& .help-kb-card': {
      fontFamily,
      ...helpTextRenderingSx,
    },
    '& .help-kb-card-title': getKnowledgeCardTitleSx(language),
    '& .help-kb-card-link': getKnowledgeCardLinkSx(language),
    '& .MuiButton-root, & .MuiButtonBase-root, & .MuiInputBase-root, & .MuiInputBase-input':
      {
        fontFamily: 'inherit',
        ...helpTextRenderingSx,
      },
  };
};
